import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Toolbar,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Fab,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FolderOpen as FolderIcon,
  InsertDriveFile as FileIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { globalStyles } from "../../Utils/Styles";
import API from "../../API";
import useAuth from "../../Hooks/use-auth";

const AssetOrganizersPage = () => {
  const navigate = useNavigate();
  const { getCurrrentUser } = useAuth();
  const user = getCurrrentUser();

  // State
  const [organizers, setOrganizers] = useState([]);
  const [files, setFiles] = useState([]);
  const [organizerTypes, setOrganizerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [filterType, setFilterType] = useState('all');
  
  // Dialog states
  const [createOrganizerDialogOpen, setCreateOrganizerDialogOpen] = useState(false);
  const [editOrganizerDialogOpen, setEditOrganizerDialogOpen] = useState(false);
  const [addFilesDialogOpen, setAddFilesDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  
  // Form states
  const [organizerForm, setOrganizerForm] = useState({
    id: null,
    name: '',
    description: '',
    organizer_type: 'general'
  });
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [organizerToDelete, setOrganizerToDelete] = useState(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOrganizer, setMenuOrganizer] = useState(null);
  
  // Notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [organizersRes, filesRes, typesRes] = await Promise.all([
        API.files.organizers.list({ include_files: true }),
        API.files.fetch(),
        API.files.organizers.getTypes()
      ]);
      
      setOrganizers(organizersRes);
      setFiles(filesRes);
      setOrganizerTypes(typesRes);
    } catch (error) {
      console.error("Error loading data:", error);
      showSnackbar("Error loading data", "error");
    }
    setLoading(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateOrganizer = async () => {
    try {
      await API.files.organizers.create(organizerForm);
      showSnackbar("Organizer created successfully", "success");
      setCreateOrganizerDialogOpen(false);
      setOrganizerForm({ id: null, name: '', description: '', organizer_type: 'general' });
      loadData();
    } catch (error) {
      console.error("Error creating organizer:", error);
      showSnackbar("Error creating organizer", "error");
    }
  };

  const handleEditOrganizer = async () => {
    try {
      await API.files.organizers.update(organizerForm.id, {
        name: organizerForm.name,
        description: organizerForm.description,
        organizer_type: organizerForm.organizer_type
      });
      showSnackbar("Organizer updated successfully", "success");
      setEditOrganizerDialogOpen(false);
      setOrganizerForm({ id: null, name: '', description: '', organizer_type: 'general' });
      loadData();
    } catch (error) {
      console.error("Error updating organizer:", error);
      showSnackbar("Error updating organizer", "error");
    }
  };

  const handleDeleteOrganizer = async () => {
    try {
      await API.files.organizers.delete(organizerToDelete.id);
      showSnackbar("Organizer deleted successfully", "success");
      setConfirmDeleteDialogOpen(false);
      setOrganizerToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting organizer:", error);
      showSnackbar("Error deleting organizer", "error");
    }
  };

  const handleAddFilesToOrganizer = async () => {
    if (!selectedOrganizer || selectedFiles.length === 0) return;
    
    try {
      const fileIds = selectedFiles.map(file => file.id);
      await API.files.organizers.addExistingFiles(selectedOrganizer.id, { file_ids: fileIds });
      
      showSnackbar(`Added ${selectedFiles.length} file(s) to organizer`, "success");
      setAddFilesDialogOpen(false);
      setSelectedFiles([]);
      setSelectedOrganizer(null);
      loadData();
    } catch (error) {
      console.error("Error adding files:", error);
      showSnackbar("Error adding files to organizer", "error");
    }
  };

  const handleReorderFiles = async (organizerId, result) => {
    if (!result.destination) return;

    const organizer = organizers.find(org => org.id === organizerId);
    const files = Array.from(organizer.files);
    const [reorderedFile] = files.splice(result.source.index, 1);
    files.splice(result.destination.index, 0, reorderedFile);

    // Update positions
    const filePositions = files.map((file, index) => ({
      file_id: file.id,
      position: index + 1
    }));

    try {
      await API.files.organizers.reorderFiles(organizerId, { file_positions: filePositions });
      loadData();
    } catch (error) {
      console.error("Error reordering files:", error);
      showSnackbar("Error reordering files", "error");
    }
  };

  const handleMenuOpen = (event, organizer) => {
    setAnchorEl(event.currentTarget);
    setMenuOrganizer(organizer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuOrganizer(null);
  };

  const openEditDialog = (organizer) => {
    setOrganizerForm({
      id: organizer.id,
      name: organizer.name,
      description: organizer.description || '',
      organizer_type: organizer.organizer_type
    });
    setEditOrganizerDialogOpen(true);
    handleMenuClose();
  };

  const openDeleteDialog = (organizer) => {
    setOrganizerToDelete(organizer);
    setConfirmDeleteDialogOpen(true);
    handleMenuClose();
  };

  const openAddFilesDialog = (organizer) => {
    setSelectedOrganizer(organizer);
    // Filter out files already in this organizer
    const availableFiles = files.filter(file => 
      !organizer.files?.some(orgFile => orgFile.id === file.id)
    );
    setAddFilesDialogOpen(true);
    handleMenuClose();
  };

  // Filter organizers by type
  const filteredOrganizers = filterType === 'all' 
    ? organizers 
    : organizers.filter(org => org.organizer_type === filterType);

  // Get available files for selected organizer
  const availableFilesForOrganizer = selectedOrganizer 
    ? files.filter(file => !selectedOrganizer.files?.some(orgFile => orgFile.id === file.id))
    : [];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f5f5f5", width: "100%", color: "#000" }}>
      <Toolbar />
      <Container sx={{ ...globalStyles.wholeHeightMinusToolbar, pt: 6, pb: 4 }}>
        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Asset Organizers
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOrganizerDialogOpen(true)}
          >
            Create Organizer
          </Button>
        </Box>

        {/* Filter Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label={`All (${organizers.length})`} />
            <Tab label="By Type" />
          </Tabs>
        </Box>

        {tabValue === 1 && (
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={[{ value: 'all', label: 'All Types' }, ...organizerTypes]}
              getOptionLabel={(option) => option.label}
              value={organizerTypes.find(type => type.value === filterType) || { value: 'all', label: 'All Types' }}
              onChange={(event, newValue) => setFilterType(newValue?.value || 'all')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Type"
                  variant="outlined"
                  sx={{ width: 300 }}
                />
              )}
            />
          </Box>
        )}

        {/* Organizers Grid */}
        {filteredOrganizers.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {filterType === 'all' ? 'No organizers yet' : `No ${filterType} organizers`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first organizer to start organizing your files
              </Typography>
              <Button
                variant="contained"
                onClick={() => setCreateOrganizerDialogOpen(true)}
              >
                Create Organizer
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredOrganizers.map((organizer) => (
              <Grid item xs={12} md={6} lg={4} key={organizer.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {organizer.name}
                        </Typography>
                        <Chip 
                          label={organizer.organizer_type} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, organizer)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {organizer.description || 'No description'}
                    </Typography>
                    
                    <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
                      {organizer.file_count || 0} files
                    </Typography>

                    {organizer.files && organizer.files.length > 0 && (
                      <DragDropContext onDragEnd={(result) => handleReorderFiles(organizer.id, result)}>
                        <Droppable droppableId={`organizer-${organizer.id}`}>
                          {(provided) => (
                            <List
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              dense
                              sx={{ maxHeight: 200, overflow: 'auto' }}
                            >
                              {organizer.files.slice(0, 3).map((file, index) => (
                                <Draggable key={file.id} draggableId={`file-${file.id}`} index={index}>
                                  {(provided) => (
                                    <ListItem
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      sx={{ px: 0 }}
                                    >
                                      <ListItemAvatar>
                                        <Avatar sx={{ width: 24, height: 24 }}>
                                          <FileIcon fontSize="small" />
                                        </Avatar>
                                      </ListItemAvatar>
                                      <ListItemText
                                        primary={file.filename}
                                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                      />
                                    </ListItem>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                              {organizer.files.length > 3 && (
                                <ListItem sx={{ px: 0 }}>
                                  <ListItemText
                                    primary={`+${organizer.files.length - 3} more files`}
                                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                  />
                                </ListItem>
                              )}
                            </List>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}
                  </CardContent>
                  
                  <CardActions>
                    <Button 
                      size="small" 
                      onClick={() => openAddFilesDialog(organizer)}
                      disabled={availableFilesForOrganizer.length === 0}
                    >
                      Add Files
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => navigate(`/organizers/${organizer.id}`)}
                    >
                      View Details
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => openEditDialog(menuOrganizer)}>
            <EditIcon sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={() => openAddFilesDialog(menuOrganizer)}>
            <AddIcon sx={{ mr: 1 }} />
            Add Files
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => openDeleteDialog(menuOrganizer)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Create Organizer Dialog */}
        <Dialog open={createOrganizerDialogOpen} onClose={() => setCreateOrganizerDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Organizer</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Organizer Name"
              fullWidth
              variant="outlined"
              value={organizerForm.name}
              onChange={(e) => setOrganizerForm({...organizerForm, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={organizerForm.description}
              onChange={(e) => setOrganizerForm({...organizerForm, description: e.target.value})}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              options={organizerTypes}
              getOptionLabel={(option) => option.label}
              value={organizerTypes.find(type => type.value === organizerForm.organizer_type) || null}
              onChange={(event, newValue) => {
                setOrganizerForm({...organizerForm, organizer_type: newValue?.value || 'general'});
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organizer Type"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOrganizerDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateOrganizer} 
              variant="contained"
              disabled={!organizerForm.name}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Organizer Dialog */}
        <Dialog open={editOrganizerDialogOpen} onClose={() => setEditOrganizerDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Organizer</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Organizer Name"
              fullWidth
              variant="outlined"
              value={organizerForm.name}
              onChange={(e) => setOrganizerForm({...organizerForm, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={organizerForm.description}
              onChange={(e) => setOrganizerForm({...organizerForm, description: e.target.value})}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              options={organizerTypes}
              getOptionLabel={(option) => option.label}
              value={organizerTypes.find(type => type.value === organizerForm.organizer_type) || null}
              onChange={(event, newValue) => {
                setOrganizerForm({...organizerForm, organizer_type: newValue?.value || 'general'});
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organizer Type"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOrganizerDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleEditOrganizer} 
              variant="contained"
              disabled={!organizerForm.name}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Files Dialog */}
        <Dialog open={addFilesDialogOpen} onClose={() => setAddFilesDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Add Files to {selectedOrganizer?.name}</DialogTitle>
          <DialogContent>
            <Autocomplete
              multiple
              options={availableFilesForOrganizer}
              getOptionLabel={(option) => option.filename}
              value={selectedFiles}
              onChange={(event, newValue) => setSelectedFiles(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Files"
                  placeholder="Choose files to add..."
                  margin="normal"
                  fullWidth
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.filename}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddFilesDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddFilesToOrganizer} 
              variant="contained"
              disabled={selectedFiles.length === 0}
            >
              Add {selectedFiles.length} File(s)
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Delete Dialog */}
        <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)}>
          <DialogTitle>Delete Organizer</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{organizerToDelete?.name}"? 
              This will remove the organizer but not the files themselves.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteOrganizer} 
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({...snackbar, open: false})}
        >
          <Alert 
            onClose={() => setSnackbar({...snackbar, open: false})} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AssetOrganizersPage;